import { useQuery } from '@tanstack/react-query';
import { QueryKeys } from '@gokrew/models/constants';
import { getDesignationList } from '@/api/designation';
import { getDepartmentList } from '@/api/department';
import { useAuth } from '@/context/AuthContext';
import type { GetListResponse } from '@/types/generic';
import type { HireJobPrefill } from '@/types/employee';
import type { OrgUnit } from '@/types/OrgUnit';

const PAGE_SIZE = 100;
const MAX_PAGES = 10;

/** Every page of a list, up to `MAX_PAGES` of `PAGE_SIZE`. */
const fetchAllPages = async <T>(
  fetchPage: (page: number) => Promise<GetListResponse<T>>
) => {
  const all: T[] = [];
  for (let page = 1; page <= MAX_PAGES; page++) {
    const { data } = await fetchPage(page);
    all.push(...(data?.data ?? []));
    if (!data?.meta?.hasNextPage) break;
  }
  return all;
};

/**
 * The company's designations, and its departments when the job names one,
 * in every branch the user can see and any status, so a hire's job can be
 * matched by name without depending on the picker's first page or its
 * accent-sensitive search. Loads nothing without a job.
 */
export const useHireJobOrgUnits = (job: HireJobPrefill | undefined) => {
  const { companyId } = useAuth();

  const designations = useQuery<OrgUnit[]>({
    queryKey: [QueryKeys.Designations, 'hire-match', companyId],
    queryFn: () =>
      fetchAllPages((page) =>
        getDesignationList(companyId!, { page, limit: PAGE_SIZE })
      ),
    enabled: !!companyId && !!job,
  });

  const departments = useQuery<OrgUnit[]>({
    queryKey: [QueryKeys.Departments, 'hire-match', companyId],
    queryFn: () =>
      fetchAllPages((page) =>
        getDepartmentList(companyId!, { page, limit: PAGE_SIZE })
      ),
    enabled: !!companyId && !!job?.department?.trim(),
  });

  return {
    designations: designations.data,
    /** Undefined while loading; empty when the job names no department. */
    departments: job?.department?.trim() ? departments.data : [],
    isError: designations.isError || departments.isError,
  };
};
